import { c as _c } from "react/compiler-runtime";
import { IcoPasskey40, IcoWarning40 } from '@onefootprint/icons';
import { HeaderTitle, NavigationHeader } from '@onefootprint/idv2';
import { getErrorMessage } from '@onefootprint/request';
import { UserChallengeActionKind } from '@onefootprint/types';
import type { PasskeyAttemptContext } from '@onefootprint/types/src/api/skip-liveness';
import { SkipLivenessClientType, SkipLivenessReason } from '@onefootprint/types/src/api/skip-liveness';
import { BottomSheet, Button, LinkButton } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogger, trackAction } from '@/idv/utils';
import { useSkipLiveness } from '../../../../queries';
import { FPCustomEvents, sendCustomEvent } from '../../../../utils/custom-event';
import useLivenessMachine from '../../hooks/use-liveness-machine';
import useBiometricInit, { isRegisterPasskeyError } from '../../hooks/use-register-biometric';
const SUCCESS_TRANSITION_DELAY_MS = 2500;
const {
  logWarn,
  logInfo,
  logTrack
} = getLogger({
  location: 'passkeys-register'
});
const Register = t0 => {
  const $ = _c(88);
  const {
    actionKind,
    onCustomSkip
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "liveness.pages.register"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t1);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [state, send] = useLivenessMachine();
  const {
    idvContext: t2
  } = state.context;
  const {
    authToken
  } = t2;
  const biometricInitMutation = useBiometricInit();
  const skipLivenessMutation = useSkipLiveness();
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [passkeyRegisterAttempts, setPasskeyRegisterAttempts] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  useEffect(_temp, t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowBottomSheet(true);
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const handleOpenBottomSheet = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShowBottomSheet(false);
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const handleCloseBottomSheet = t6;
  let t7;
  if ($[5] !== actionKind || $[6] !== authToken || $[7] !== biometricInitMutation || $[8] !== passkeyRegisterAttempts || $[9] !== send) {
    t7 = () => {
      if (biometricInitMutation.isPending) {
        return;
      }
      biometricInitMutation.mutate({
        authToken,
        actionKind
      }, {
        onSuccess(t8) {
          const {
            deviceResponseJson
          } = t8;
          logTrack("Passkeys registration succeeded");
          setTimeout(() => {
            sendCustomEvent(FPCustomEvents.receivedDeviceResponseJson, {
              deviceResponseJson
            });
            send({
              type: "succeeded"
            });
          }, SUCCESS_TRANSITION_DELAY_MS);
        },
        onError(error) {
          let e = error;
          let elapsedTimeInOsPromptMs;
          if (isRegisterPasskeyError(error)) {
            e = error.error;
            elapsedTimeInOsPromptMs = error.elapsedTimeInOsPromptMs;
          }
          const errorMessage = getErrorMessage(e);
          logWarn(`Failed to register passkeys for user: ${errorMessage}`, e);
          const passkeyFailure = {
            errorMessage,
            elapsedTimeInOsPromptMs
          };
          setPasskeyRegisterAttempts([...passkeyRegisterAttempts, passkeyFailure]);
        }
      });
    };
    $[5] = actionKind;
    $[6] = authToken;
    $[7] = biometricInitMutation;
    $[8] = passkeyRegisterAttempts;
    $[9] = send;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleRegister = t7;
  let t8;
  if ($[11] !== onCustomSkip || $[12] !== send) {
    t8 = () => {
      trackAction("passkeys:skipped");
      send({
        type: "skipped"
      });
      onCustomSkip?.();
    };
    $[11] = onCustomSkip;
    $[12] = send;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleCustomSkip = t8;
  let t9;
  if ($[14] !== authToken || $[15] !== passkeyRegisterAttempts || $[16] !== send || $[17] !== skipLivenessMutation) {
    t9 = () => {
      if (skipLivenessMutation.isPending) {
        return;
      }
      trackAction("passkeys:skipped");
      const context = {
        reason: SkipLivenessReason.failed,
        clientType: SkipLivenessClientType.web,
        numAttempts: passkeyRegisterAttempts.length,
        attempts: passkeyRegisterAttempts
      };
      skipLivenessMutation.mutate({
        authToken,
        context
      }, {
        onSuccess: () => {
          logInfo("Registered passkeys with success");
          send({
            type: "skipped"
          });
        },
        onError: _temp2
      });
    };
    $[14] = authToken;
    $[15] = passkeyRegisterAttempts;
    $[16] = send;
    $[17] = skipLivenessMutation;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleSkip = t9;
  let icon;
  let primaryButtonText;
  let secondaryButtonText;
  let headerTitle;
  let headerSubtitle;
  if (biometricInitMutation.isSuccess) {
    let t10;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <IcoPasskey40 color="primary" />;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    icon = t10;
    let t11;
    if ($[20] !== t) {
      t11 = t(_temp3);
      $[20] = t;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    headerTitle = t11;
    let t12;
    if ($[22] !== t) {
      t12 = t(_temp4);
      $[22] = t;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    headerSubtitle = t12;
  } else {
    if (passkeyRegisterAttempts.length) {
      let t10;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <IcoWarning40 color="error" />;
        $[24] = t10;
      } else {
        t10 = $[24];
      }
      icon = t10;
      let t11;
      if ($[25] !== t) {
        t11 = t(_temp5);
        $[25] = t;
        $[26] = t11;
      } else {
        t11 = $[26];
      }
      headerTitle = t11;
      let t12;
      if ($[27] !== t) {
        t12 = t(_temp6);
        $[27] = t;
        $[28] = t12;
      } else {
        t12 = $[28];
      }
      headerSubtitle = t12;
      let t13;
      if ($[29] !== t) {
        t13 = t(_temp7);
        $[29] = t;
        $[30] = t13;
      } else {
        t13 = $[30];
      }
      primaryButtonText = t13;
      let t14;
      if ($[31] !== t) {
        t14 = t(_temp8);
        $[31] = t;
        $[32] = t14;
      } else {
        t14 = $[32];
      }
      secondaryButtonText = t14;
    } else {
      let t10;
      if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <IcoPasskey40 color="primary" />;
        $[33] = t10;
      } else {
        t10 = $[33];
      }
      icon = t10;
      let t11;
      if ($[34] !== actionKind || $[35] !== t) {
        t11 = actionKind === UserChallengeActionKind.replace ? t(_temp9) : t(_temp10);
        $[34] = actionKind;
        $[35] = t;
        $[36] = t11;
      } else {
        t11 = $[36];
      }
      headerTitle = t11;
      let t12;
      if ($[37] !== t) {
        t12 = t(_temp11);
        $[37] = t;
        $[38] = t12;
      } else {
        t12 = $[38];
      }
      headerSubtitle = t12;
      let t13;
      if ($[39] !== t) {
        t13 = t(_temp12);
        $[39] = t;
        $[40] = t13;
      } else {
        t13 = $[40];
      }
      primaryButtonText = t13;
    }
  }
  let t10;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <NavigationHeader />;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== icon) {
    t11 = <div className="flex items-center justify-center mb-2">{icon}</div>;
    $[42] = icon;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== headerSubtitle || $[45] !== headerTitle) {
    t12 = <HeaderTitle title={headerTitle} subtitle={headerSubtitle} />;
    $[44] = headerSubtitle;
    $[45] = headerTitle;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== biometricInitMutation.isPending || $[48] !== handleRegister || $[49] !== primaryButtonText || $[50] !== skipLivenessMutation) {
    t13 = primaryButtonText && <Button loading={biometricInitMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={handleRegister} fullWidth={true} size="large">{primaryButtonText}</Button>;
    $[47] = biometricInitMutation.isPending;
    $[48] = handleRegister;
    $[49] = primaryButtonText;
    $[50] = skipLivenessMutation;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== biometricInitMutation.isPending || $[53] !== handleCustomSkip || $[54] !== handleSkip || $[55] !== onCustomSkip || $[56] !== secondaryButtonText || $[57] !== skipLivenessMutation) {
    t14 = secondaryButtonText && <Button loading={skipLivenessMutation.isPending} disabled={biometricInitMutation.isPending || skipLivenessMutation.isPending} onClick={typeof onCustomSkip === "function" ? handleCustomSkip : handleSkip} fullWidth={true} size="large" variant="secondary">{secondaryButtonText}</Button>;
    $[52] = biometricInitMutation.isPending;
    $[53] = handleCustomSkip;
    $[54] = handleSkip;
    $[55] = onCustomSkip;
    $[56] = secondaryButtonText;
    $[57] = skipLivenessMutation;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== biometricInitMutation.isSuccess || $[60] !== t) {
    t15 = !biometricInitMutation.isSuccess && <div className="flex items-center w-full justify-center pt-2"><LinkButton onClick={handleOpenBottomSheet}>{t(_temp13)}</LinkButton></div>;
    $[59] = biometricInitMutation.isSuccess;
    $[60] = t;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== t) {
    t16 = t(_temp14);
    $[62] = t;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== t) {
    t17 = t(_temp15);
    $[64] = t;
    $[65] = t17;
  } else {
    t17 = $[65];
  }
  let t18;
  if ($[66] !== t17) {
    t18 = <div className="text-body-2 text-primary whitespace-pre-wrap">{t17}</div>;
    $[66] = t17;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  if ($[68] !== t) {
    t19 = t(_temp16);
    $[68] = t;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  let t20;
  if ($[70] !== t19) {
    t20 = <div className="text-body-2 text-primary whitespace-pre-wrap">{t19}</div>;
    $[70] = t19;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t18 || $[73] !== t20) {
    t21 = <div className="flex gap-2 flex-col mb-4">{t18}{t20}</div>;
    $[72] = t18;
    $[73] = t20;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  let t22;
  if ($[75] !== showBottomSheet || $[76] !== t16 || $[77] !== t21) {
    t22 = <BottomSheet open={showBottomSheet} onClose={handleCloseBottomSheet} title={t16} portalId="passkeys-register-container">{t21}</BottomSheet>;
    $[75] = showBottomSheet;
    $[76] = t16;
    $[77] = t21;
    $[78] = t22;
  } else {
    t22 = $[78];
  }
  let t23;
  if ($[79] !== t13 || $[80] !== t14 || $[81] !== t15 || $[82] !== t22) {
    t23 = <div className="flex flex-col mt-6 w-full gap-3">{t13}{t14}{t15}{t22}</div>;
    $[79] = t13;
    $[80] = t14;
    $[81] = t15;
    $[82] = t22;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== t11 || $[85] !== t12 || $[86] !== t23) {
    t24 = <div id="passkeys-register-container" className="flex flex-col items-center h-full justify-center text-center"><div>{t10}{t11}{t12}{t23}</div></div>;
    $[84] = t11;
    $[85] = t12;
    $[86] = t23;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  return t24;
};
export default Register;
function _temp() {
  logInfo("Passkeys registration started");
}
function _temp2(error_0) {
  logWarn(`Failed to skip liveness after retrying registering passkeys: ${getErrorMessage(error_0)}`, error_0);
}
function _temp3($) {
  return $.success.title;
}
function _temp4($_0) {
  return $_0.success.subtitle;
}
function _temp5($_1) {
  return $_1.retry.title;
}
function _temp6($_2) {
  return $_2.retry.subtitle;
}
function _temp7($_3) {
  return $_3.retry.cta;
}
function _temp8($_4) {
  return $_4.retry.skip;
}
function _temp9($_5) {
  return $_5["title-replace"];
}
function _temp10($_6) {
  return $_6["title-add"];
}
function _temp11($_7) {
  return $_7.subtitle;
}
function _temp12($_8) {
  return $_8.cta;
}
function _temp13($_9) {
  return $_9["learn-more"].cta;
}
function _temp14($_10) {
  return $_10["learn-more"].title;
}
function _temp15($_11) {
  return $_11["learn-more"]["content-1"];
}
function _temp16($_12) {
  return $_12["learn-more"]["content-2"];
}